import { Announcer } from "../models/announcer.js";

/** @type {Announcer | null} */
let announcer = null;

/**
 * Add a message to an announcer log
 * @param {Object} args - object for named arguments
 * @param {string} args.message
 * @param {string} [args.level="polite"]
 * @param {number} [args.timeout=0]
 */
export function announce({ message, level = "polite", timeout = 0 }) {
  if (!announcer) {
    announcer = new Announcer();
  }

  announcer.announce({ message, level, timeout });
}

/**
 * Clear the announcer log for the specified level
 * @param {string} [level=""]
 */
export function clear(level = "") {
  if (announcer) {
    announcer.clear(level);
  } else {
    console.error("No announcer available in clear()");
  }
}

/**
 * Remove the announcer node from the DOM
 */
export function remove() {
  if (announcer) {
    announcer.remove();
    announcer = null;
  } else {
    console.error("No announcer available in remove()");
  }
}

/**
 * Get the last item in the specified announcer log
 * @param {string} [level="polite"]
 */
export function getLastItem(level = "polite") {
  if (announcer) {
    if (level === "assertive") {
      return announcer.assertiveLog.lastElementChild;
    }
    if (level === "polite") {
      return announcer.politeLog.lastElementChild;
    }
    if (level === "focus") {
      return announcer.focusLog.lastElementChild;
    }
    console.error(
      "Invalid level - valid options are 'polite', 'assertive', or 'focus'."
    );
    return null;
  }
  console.error("No announcer availble in focusLastItem()");
  return null;
}

/**
 * Get all the message nodes in the specified level log
 * @param {string} [level="polite"]
 */
export function getAllMessages(level = "polite") {
  if (announcer) {
    if (level === "polite") {
      return announcer.politeLog.children;
    }
    if (level === "assertive") {
      return announcer.assertiveLog.children;
    }
    if (level === "focus") {
      return announcer.focusLog.children;
    }
    console.error(
      "Invalid level. Valid options are 'polite', 'assertive', and 'focus'"
    );
    return null;
  }
  console.error("No announcer available in getAllMessages()");
  return null;
}

/**
 * Add a message to the specified level log without speaking it with VO. We do
 * this by removing the attributes that make this a listen-able element before
 * adding the message, and then adding them back afterward
 * @param {Object} args - object for named arguments
 * @param {string} args.message
 * @param {string} [args.level="polite"]
 * @returns null
 */
export function silentAnnounce({ message, level = "polite" }) {
  if (!announcer) {
    announcer = new Announcer();
  }
  if (announcer) {
    if (!["polite", "assertive", "focus"].includes(level)) {
      console.error(
        "Invalid level. Valid options are 'polite', 'assertive', and 'focus'"
      );
      return null;
    }
    announcer[`${level}Log`].removeAttribute("role");
    announcer[`${level}Log`].removeAttribute("aria-live");
    announcer[`${level}Log`].removeAttribute("aria-relevant");
    announcer.addSilently({ message, level });
  }
  console.error("No announcer available in silentAnnounce()");
  return null;
}

/**
 * Get the log wrapper node
 * @param {string} level
 * @returns {HTMLElement | null}
 */
export function getLog(level = "polite") {
  if (announcer) {
    return announcer[`${level}Log`];
  }
  return null;
}
